import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux';
import { logOut, verifyEmail, verifyEmailOtp } from '../logic/login/action';
import logo from '../images/logo_clock.png'
import {Link, useLocation, useNavigate} from 'react-router-dom'
import { validateEmail } from '../utils/validators';
import { motion } from 'framer-motion';
import ThreeBackground from '../components/ThreeBackground';
import { Button, Box, Typography } from '@mui/material';
import { MdArrowForward } from 'react-icons/md';
import ModernInput from '../components/ModernInput';
import OtpInput from '../components/OtpInput';
import { Formik, Form, Field } from 'formik';

const ForgotPasswordVerify = () => {
  const [data, setData] = useState({
    email: "",
    otp: ""
  });
  
  const route = useLocation();
  
  const superadmin = useMemo(() => {
    const dt = new URLSearchParams(route.search);
    if (dt.get('email')) {
      return dt.get('email');
    } else {
      return '';
    }
  }, [route.search]);
  
  const verifyOnly = useMemo(() => {
    const dt = new URLSearchParams(route.search);
    if (dt.get('verifyOnly')) {
      return true;
    } else {
      return false;
    }
  }, [route.search]);

  const [sendEmailSuccess, setsendEmailSuccess] = useState(false);
  const [error, seterror] = useState(false);
  const [errorEmail, seterrorEmail] = useState(false);
  
  const dispatch = useDispatch();
  const nav = useNavigate();
  
  useEffect(() => {
    seterror(false);
    seterrorEmail(false);
    setsendEmailSuccess(false);
    if (superadmin !== '' && data.email === '') {
      setData(prev => ({ ...prev, email: superadmin }))
    }
  }, [superadmin]);
  
  const onSubmit = async (values) => {
    if (sendEmailSuccess) {
      seterrorEmail(false);
      seterror(true);
      if (values.otp && values.otp.length === 4) {
        seterror(false);
        await verifyEmailOtp(dispatch, data.email, values.otp, () => verifyOnly ?
          nav('/admin/complete-setup')
          : nav('/admin/change-password?email=' + data.email))
      } else {
        seterror(true);
      }
    } else {
      seterrorEmail('Please enter valid email');
      seterror(false);
      if (validateEmail(values.email)) {
        seterrorEmail(false);
        const dt = await verifyEmail(dispatch, values.email);
        if (typeof dt === 'string') {
          seterrorEmail(dt);
        } else if (typeof dt === 'boolean') {
          seterrorEmail(false);
          setsendEmailSuccess(true);
          setData(prev => ({ ...prev, email: values.email }));
        }
      } else {
        seterrorEmail('Please enter a valid email address');
      }
    }
  }

  return (
    <div className="w-screen h-screen bg-gradient-to-br from-[#667eea] via-[#764ba2] to-[#0B469D] relative overflow-hidden flex flex-col">
      <ThreeBackground />

      {/* Header */}
      <motion.header
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6 }}
        className="w-full flex items-center py-2 px-3 sm:px-4 md:px-6 lg:px-8 flex-shrink-0 relative z-10"
      >
        <motion.a
          href="/"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="flex-shrink-0"
        >
          <img
            src={logo}
            className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 drop-shadow-lg"
            alt="Logo"
          />
        </motion.a>
      </motion.header>

      {/* Main Content */}
      <div className="w-full flex-1 flex items-center px-2 sm:px-3 md:px-4 lg:px-6 relative z-10 overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 md:gap-6 lg:gap-8 items-center w-full h-full max-w-7xl mx-auto">
          {/* Left Side - Content */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="hidden lg:flex flex-col gap-2 sm:gap-3 text-white h-full justify-center"
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: "2.5rem", xl: "3rem" },
                  fontWeight: "bold",
                  lineHeight: 1.2,
                  mb: 2,
                  background: "linear-gradient(135deg, #ffffff 0%, #e0e7ff 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Verify Your Account
                <span className="text-[#72a9fb]">.</span>
              </Typography>
            </motion.div>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              sx={{
                fontSize: { lg: "1rem", xl: "1.125rem" },
                lineHeight: 1.5,
                color: "rgba(255, 255, 255, 0.9)",
                mb: 2,
              }}
            >
              {sendEmailSuccess
                ? "Enter the 4-digit OTP sent to your email to verify your account."
                : "Enter your email address to receive a verification code."}
            </motion.p>
          </motion.div>

          {/* Right Side - Form */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex justify-center w-full h-full items-center"
          >
            <motion.div
              transition={{ duration: 0.3 }}
              className="w-full max-w-md lg:max-w-lg h-full flex items-center"
            >
              <Box
                sx={{
                  backgroundColor: "rgba(255, 255, 255, 0.98)",
                  backdropFilter: "blur(20px)",
                  borderRadius: "20px",
                  padding: { xs: 2.5, sm: 3, md: 3.5 },
                  boxShadow: "0 25px 70px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.5)",
                  border: "1px solid rgba(255, 255, 255, 0.3)",
                  height: { xs: "100%", lg: "fit-content" },
                  maxHeight: { xs: "100%", lg: "90vh" },
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                  width: "100%",
                }}
              >
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="flex flex-col h-full"
                >
                  <Typography
                    variant="h4"
                    sx={{
                      fontSize: { xs: "1.375rem", sm: "1.625rem", md: "1.875rem" },
                      fontWeight: 700,
                      color: "#111827",
                      mb: 1,
                      letterSpacing: "-0.02em",
                    }}
                  >
                    {sendEmailSuccess ? "Enter OTP" : "Verify Your Account"}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: { xs: "0.75rem", sm: "0.8125rem", md: "0.875rem" },
                      color: "#4b5563",
                      mb: { xs: 2, sm: 2.5 },
                    }}
                  >
                    {sendEmailSuccess
                      ? `Enter the 4-digit code sent to ${data.email}`
                      : "Enter your email to receive verification code"}
                  </Typography>

                  {/* Form */}
                  <Formik
                    initialValues={{
                      email: data.email || '',
                      otp: data.otp || ''
                    }}
                    enableReinitialize
                    onSubmit={onSubmit}
                  >
                    {({ isSubmitting, values }) => (
                      <Form className="flex flex-col gap-4">
                        {!sendEmailSuccess ? (
                          <div>
                            <ModernInput
                              comp={{
                                name: 'email',
                                label: 'Email',
                                placeholder: 'Enter your email here',
                                type: 'email'
                              }}
                            />
                            {errorEmail && (
                              <Typography
                                variant="caption"
                                sx={{ color: 'error.main', mt: 0.5, display: 'block' }}
                              >
                                {errorEmail}
                              </Typography>
                            )}
                          </div>
                        ) : (
                          <>
                            <div>
                              <Field name="otp">
                                {({ field, form }) => (
                                  <OtpInput
                                    value={field.value}
                                    onChange={(e) => {
                                      form.setFieldValue('otp', e.target.value);
                                      form.setFieldTouched('otp', true);
                                    }}
                                    error={error && values.otp?.length !== 4}
                                    label="Enter OTP"
                                  />
                                )}
                              </Field>
                            </div>
                            <div className="w-full text-end">
                              <Typography
                                component="button"
                                type="button"
                                onClick={() => verifyEmail(dispatch, data.email)}
                                sx={{
                                  color: "#0B469D",
                                  fontSize: "0.875rem",
                                  fontWeight: 500,
                                  cursor: "pointer",
                                  textDecoration: "none",
                                  background: "none",
                                  border: "none",
                                  padding: 0,
                                  "&:hover": {
                                    textDecoration: "underline",
                                    color: "#72a9fb",
                                  },
                                }}
                              >
                                Resend OTP
                              </Typography>
                            </div>
                          </>
                        )}

                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.5 }}
                        >
                          <motion.div whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.99 }}>
                            <Button
                              type="submit"
                              disabled={isSubmitting}
                              fullWidth
                              variant="contained"
                              size="small"
                              endIcon={<MdArrowForward size={18} />}
                              sx={{
                                borderRadius: "10px",
                                py: { xs: 0.875, sm: 1.125 },
                                background: "linear-gradient(135deg, #0B469D 0%, #72a9fb 100%)",
                                textTransform: "none",
                                fontSize: { xs: "0.8125rem", sm: "0.875rem" },
                                fontWeight: 600,
                                boxShadow: "0 6px 20px rgba(11, 70, 157, 0.35), 0 2px 8px rgba(11, 70, 157, 0.2)",
                                "&:hover": {
                                  background: "linear-gradient(135deg, #0a3d7a 0%, #5a8fd8 100%)",
                                  boxShadow: "0 8px 25px rgba(11, 70, 157, 0.45), 0 4px 12px rgba(11, 70, 157, 0.25)",
                                  transform: "translateY(-1px)",
                                },
                                "&:disabled": {
                                  background: "linear-gradient(135deg, #9ca3af 0%, #9ca3af 100%)",
                                  opacity: 1,
                                  cursor: "not-allowed",
                                  pointerEvents: "none",
                                },
                              }}
                            >
                              {isSubmitting
                                ? "Verifying..."
                                : sendEmailSuccess
                                ? "Verify OTP"
                                : "Send OTP"}
                            </Button>
                          </motion.div>
                        </motion.div>

                        <div className="pt-3 border-t border-gray-200 mt-2">
                          <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.6 }}
                            className="text-center"
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                color: "#6b7280",
                                fontSize: { xs: "0.75rem", sm: "0.8125rem" },
                              }}
                            >
                              Don't have an account?{" "}
                              <Link
                                to="/admin/register"
                                onClick={() => logOut(dispatch)}
                                className="text-[#0B469D] hover:text-[#72a9fb] font-semibold transition-colors underline decoration-transparent hover:decoration-[#0B469D]"
                              >
                                Sign Up
                              </Link>
                            </Typography>
                          </motion.div>
                        </div>
                      </Form>
                    )}
                  </Formik>
                </motion.div>
              </Box>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </div>
  )
}

export default ForgotPasswordVerify;